import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '@/services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, shipping_total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated || !token) {
      // Load from localStorage for non-authenticated users
      const localCart = localStorage.getItem('byggfynd_cart');
      if (localCart) {
        setCart(JSON.parse(localCart));
      }
      return;
    }

    setLoading(true);
    try {
      const response = await cartApi.get();
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, productData = null) => {
    // Track AddToCart för Meta Pixel
    if (productData && window.trackAddToCart) {
      window.trackAddToCart({
        id: productId,
        title: productData.title,
        category: productData.category_id,
        price: productData.price
      }, quantity);
    }
    
    if (!isAuthenticated) {
      // Handle local cart for non-authenticated users
      const localCart = { ...cart };
      const existingItem = localCart.items.find(i => i.product_id === productId);
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        // Include product data for display
        localCart.items.push({ 
          product_id: productId, 
          quantity,
          title: productData?.title || 'Produkt',
          price: productData?.price || 0,
          shipping_cost: productData?.shipping_cost || 0,
          image: productData?.images?.[0] || ''
        });
      }
      // Recalculate totals
      localCart.total = localCart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      localCart.shipping_total = localCart.items.reduce((sum, item) => sum + (item.shipping_cost || 0), 0);
      localCart.item_count = localCart.items.reduce((sum, item) => sum + item.quantity, 0);
      
      setCart(localCart);
      localStorage.setItem('byggfynd_cart', JSON.stringify(localCart));
      return localCart;
    }

    setLoading(true);
    try {
      const response = await cartApi.add(productId, quantity);
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, quantity) => {
    if (!isAuthenticated) {
      const localCart = { ...cart };
      if (quantity <= 0) {
        localCart.items = localCart.items.filter(i => i.product_id !== productId);
      } else {
        const item = localCart.items.find(i => i.product_id === productId);
        if (item) item.quantity = quantity;
      }
      setCart(localCart);
      localStorage.setItem('byggfynd_cart', JSON.stringify(localCart));
      return localCart;
    }

    setLoading(true);
    try {
      const response = await cartApi.update(productId, quantity);
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    if (!isAuthenticated) {
      const localCart = { ...cart };
      localCart.items = localCart.items.filter(i => i.product_id !== productId);
      setCart(localCart);
      localStorage.setItem('byggfynd_cart', JSON.stringify(localCart));
      return localCart;
    }

    setLoading(true);
    try {
      const response = await cartApi.removeItem(productId);
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0, shipping_total: 0 });
      localStorage.removeItem('byggfynd_cart');
      return;
    }

    setLoading(true);
    try {
      await cartApi.clear();
      setCart({ items: [], total: 0, shipping_total: 0 });
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    fetchCart,
    cartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
